import {
  AssetTransaction,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  getShowJSONContextItem,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useAssetTransactionColumns,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
  type WebsocketRequest,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';

import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import type { GetContextMenuItemsParams, GetMainMenuItemsParams } from 'ag-grid-community';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useAssetTransactionsFilter,
  type AssetTransactionsFilter,
} from './useAssetTransactionsFilter';

export interface AssetTransactionsBlotterTableProps {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: Column[];
  defaultFilter?: AssetTransactionsFilter;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
}

export function AssetTransactionsBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  onCloneTab,
}: AssetTransactionsBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const columns = useAssetTransactionColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useAssetTransactionsFilter({
    persistedBlotterTable,
  });

  const { blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const { handleClickJson, jsonModal } = useJsonModal();
  const defaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams) => {
    const data = params.node?.data;
    const items = compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties: filterBuilderProps.properties,
        openClause: filterBuilderAccordion.openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      ...(data ? [getShowJSONContextItem({ params, handleClickJson }), 'separator'] : []),
      ...defaultContextMenuItems(params),
    ]);
    return items;
  });

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: AssetTransaction.StreamName,
      sort_by: '-TransactTime',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, AssetTransaction>({
    request,
    rowID: AssetTransaction.rowID,
    persistence: persistedBlotterTable,
    columns,
    getExtraMainMenuItems,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    gridOptions: {
      getContextMenuItems,
    },
    exportOptions: {
      name: AssetTransaction.BlotterExportName,
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.TransactTime} />
      <BlotterTable {...blotterTable} />
      {jsonModal}
    </>
  );
}
